// Lifted from useNormalAnswersArrayData; pure so basic-output can reuse without React/Jotai.
//
// Design: "fat input, dumb function". The wrapping hook resolves Jotai context
// and sibling hooks (mapGroup, mapGroupsDiff, totalData) and passes the
// already-resolved values here, so the offline batch path and the interactive
// page share one derivation and cannot drift.

import * as R from 'remeda'

import { type Question } from '~/hooks/useSurveyQuery'
import { findByPath, getByPath, type GroupAndAll } from '~/utils'

import { type CountAnswers } from './atomTypes'
import {
  type AnswerOption,
  type GroupsDiff,
  type GroupsDiffColumnKey,
  type RowData,
} from './common'

export type NormalAnswerData = RowData<GroupAndAll | GroupsDiffColumnKey> & {
  question: Question
  answerOption: AnswerOption
}

export type NormalAnswersArrayDataInput = {
  mapGroup: <T>(
    mapFunc: (group: GroupAndAll, index: number) => T,
  ) => Record<GroupAndAll, T>
  mapGroupsDiff: <T>(
    mapFunc: (groupsDiff: GroupsDiff, index: number) => T,
  ) => Record<GroupsDiffColumnKey, T>
  questionIds: Question['question_id'][]
  questions: Question[]
  results: (CountAnswers[] | null)[]
  totalCounts: Record<GroupAndAll, number>
}

export const buildNormalAnswersArrayData = ({
  mapGroup,
  mapGroupsDiff,
  questionIds,
  questions,
  results,
  totalCounts,
}: NormalAnswersArrayDataInput): NormalAnswerData[][] =>
  R.pipe(
    R.zip(questionIds, results),
    R.map(([questionId, countAnswersArray]) => ({
      question: findByPath(questions, 'question_id', questionId) as Question,
      countAnswers: (countAnswersArray ?? [])[0],
    })),
    R.filter(({ question }) => question.mt_questions?.length === 0),
    R.map(({ countAnswers, question }) =>
      R.pipe(
        countAnswers?.answers ?? [],
        R.map((answer, answerIndex) => {
          const getGroupCount = (group: GroupAndAll) =>
            group === 'all'
              ? answer.count
              : (R.pipe(
                  countAnswers.groupAnswers ?? [],
                  findByPath('group', group),
                  getByPath(`answers.${answerIndex}.count`),
                ) ?? NaN)

          return {
            question,
            answerOption: answer.answerOption as AnswerOption,
            counts: {
              ...mapGroup(getGroupCount),
              ...mapGroupsDiff(
                ([left, right]) => getGroupCount(left) - getGroupCount(right),
              ),
            },
          }
        }),
        (answersData) =>
          answersData.map((answerData) => {
            const groupRatios = mapGroup(
              (group) => answerData.counts[group] / totalCounts[group],
            )

            const groupsToCompareRatios = mapGroupsDiff(
              ([left, right]) => groupRatios[left] - groupRatios[right],
            )

            return {
              ...answerData,
              ratios: { ...groupRatios, ...groupsToCompareRatios },
            }
          }),
      ),
    ),
  )
